// Iter52 — Clean point management panel for sorting requests whose
// `sorting_scope.type == "clean_point"`. Provides:
//   1. Register clean point DN (supplier-confirmed start of clean material)
//   2. Verify the next inbound delivery (DN+1)
//
// Mounted above the SortingResultForm on the /sorting workspace whenever the
// active SR is clean-point scoped, so the sorter is guided through the
// safety-rule "DN + 1" before they can close.
import React, { useState } from "react";
import axios from "axios";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export const CleanPointPanel = ({ sr, onUpdated }) => {
  const addToast = useStore((s) => s.addToast);
  const scope = sr?.sorting_scope || {};
  const [dn, setDn] = useState("");
  const [plusOneDn, setPlusOneDn] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  if (scope.type !== "clean_point") return null;

  const registered = Boolean(scope.clean_point_dn);
  const verified = Boolean(scope.clean_point_plus_one_verified);

  const registerCP = async () => {
    if (!dn.trim()) {
      addToast({ type: "error", title: "Enter the clean-point DN" });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-requests/${sr.id}/clean-point`, {
        clean_point_dn: dn.trim(),
      });
      addToast({ type: "success", title: "Clean point registered", body: `DN ${dn.trim()}` });
      setDn("");
      onUpdated?.(r.data);
    } catch (e) {
      addToast({ type: "error", title: "Failed to register clean point", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const verifyPlusOne = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-requests/${sr.id}/clean-point/verify-plus-one`, {
        clean_point_plus_one_dn: plusOneDn.trim() || null,
        notes: notes.trim() || null,
      });
      addToast({ type: "success", title: "DN+1 verified", body: "Sorting request can now be closed" });
      setPlusOneDn("");
      setNotes("");
      onUpdated?.(r.data);
    } catch (e) {
      addToast({ type: "error", title: "Failed to verify DN+1", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const stateLabel = !registered ? "Awaiting clean-point DN"
    : !verified ? `Clean point ${scope.clean_point_dn} — DN+1 not yet verified`
    : `Clean point ${scope.clean_point_dn} · DN+1 ${scope.clean_point_plus_one_dn || ""} verified`;
  const stateColor = !registered ? "#c89adb" : !verified ? "#f3b14a" : "#9aebc2";

  return (
    <div
      className="rounded-md p-4 mb-4"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(243,177,74,0.30)",
      }}
      data-testid="clean-point-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text, #f0f2f8)" }}>
          Clean point
        </span>
        <span style={{ ...mono(9, stateColor, 700, "0.8px"), textTransform: "uppercase" }} data-testid="clean-point-state">
          · {stateLabel}
        </span>
      </div>

      {!registered && (
        <div className="flex gap-2 items-end" data-testid="clean-point-register-row">
          <div className="flex-1">
            <div className="uppercase mb-1" style={mono(9, "#9198b0", 600, "0.8px")}>Clean-point DN</div>
            <input
              type="text"
              value={dn}
              onChange={(e) => setDn(e.target.value)}
              placeholder="e.g. DN-12345"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
              data-testid="clean-point-dn-input"
            />
          </div>
          <button
            type="button"
            onClick={registerCP}
            disabled={busy}
            className="px-3 py-2 rounded-md"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", border: "none", opacity: busy ? 0.6 : 1 }}
            data-testid="clean-point-register-btn"
          >Register clean point</button>
        </div>
      )}

      {registered && !verified && (
        <div className="space-y-2" data-testid="clean-point-verify-row">
          <div
            className="px-3 py-2 rounded"
            style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.30)", ...mono(11, "#f3b14a") }}
            data-testid="clean-point-safety-warning"
          >
            ⚠ Safety rule: ONE more inbound delivery after DN <strong>{scope.clean_point_dn}</strong> must be inspected before this sorting request can close.
          </div>
          <div>
            <div className="uppercase mb-1" style={mono(9, "#9198b0", 600, "0.8px")}>DN+1 delivery note (optional but recommended)</div>
            <input
              type="text"
              value={plusOneDn}
              onChange={(e) => setPlusOneDn(e.target.value)}
              placeholder="e.g. DN-12346"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
              data-testid="clean-point-plus-one-dn-input"
            />
          </div>
          <div>
            <div className="uppercase mb-1" style={mono(9, "#9198b0", 600, "0.8px")}>Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="What did you inspect? Results?"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11), resize: "vertical" }}
              data-testid="clean-point-verify-notes"
            />
          </div>
          <button
            type="button"
            onClick={verifyPlusOne}
            disabled={busy}
            className="px-3 py-2 rounded-md"
            style={{ background: "#4ed084", color: "#08090d", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", border: "none", opacity: busy ? 0.6 : 1 }}
            data-testid="clean-point-verify-btn"
          >✓ Verify DN+1 — unlock closure</button>
        </div>
      )}

      {verified && (
        <div
          className="px-3 py-2 rounded"
          style={{ background: "rgba(78,208,132,0.10)", border: "1px solid rgba(78,208,132,0.30)", ...mono(11, "#9aebc2") }}
          data-testid="clean-point-verified-banner"
        >
          ✓ Clean point <strong>{scope.clean_point_dn}</strong> with DN+1 <strong>{scope.clean_point_plus_one_dn || "—"}</strong> verified. The sorting request can be closed.
        </div>
      )}
    </div>
  );
};
